import React, { useEffect, useState } from 'react';
import { CoreDropdown } from '@komodo-design-system/komodo-components-react';
import { MetadataElement, MetadataElementList } from './SingleUploadModel';
import './SingleUploadContent.scss';
import { ThunkDispatch } from 'redux-thunk';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { singleUploadActions } from 'actions/singleUpload';
import { isEmpty } from 'lodash-es';
import { findElementByPrompt, getAllElementsByCascadeTableId } from 'utils/utils';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
import { targetsiteErp } from 'actions/targetsiteErpMapping';

interface DropdownFieldProps {
  element: MetadataElement;
  rowIndex?: number | null;
  assetId?: string;
}

const DropdownFieldWrapperCascade: React.FC<DropdownFieldProps> = ({
  element,
  rowIndex,
  assetId,
}) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const [alert, setAlert] = useState({ show: false, message: '' });
  const cascadeDropdownOptions = useSelector(
    (state: any) => state.cascaseDropDownOptions?.cascadingDropDownOptions
  );
  const cascadingStates = useSelector((state: any) => state.cascadeTableIDApiValues?.dynamicState) || {};
  const singleUploadPayload = useSelector((state: any) => state.singleUpload.payload);
  const assetTypes = useSelector((state: any) => state.assetTypeReducer?.assetTypes);
  const { eventType } = useSelector((state: any) => state.submitValidationReducer);

  const elementWithCascadeTableId = getAllElementsByCascadeTableId(singleUploadPayload, element.cascade_table_id);

  // Fetch dropdown options dynamically
  const fetchDropdownOptions = async () => {
    const assetTypeDefaults = assetTypes?.metadata_collection?.DEFAULTS?.[singleUploadPayload.metadata_model_id] || {};

    if (element.is_cascade === 'Y' && element.is_tabular === 'N') {
      const index = elementWithCascadeTableId.findIndex((e: MetadataElement) => e.field_id === element.field_id);

      if (element.cascade_table_id && !cascadingStates[element.cascade_table_id] && index === 0) {
        dispatch(targetsiteErp.fetchCascasdeDropdownValues(element.cascade_table_id as string));
        return;
      }

      let formattedOptions: any[] = [];

      if (index > 0) {
        formattedOptions = getFilteredOptionsForCascading(index, assetTypeDefaults);
        if (assetTypeDefaults?.[element.field_id]) {
          const defaultValue = assetTypeDefaults?.[element.field_id];
          const defaultOption = getDefaultOption(defaultValue, formattedOptions);
          const value = findElementByPrompt(singleUploadPayload,elementWithCascadeTableId[index].prompt);
          if (defaultOption && isEmpty(assetId) && !value?.value?.id) {
            handleDropdownChange(defaultOption[0]);
          }
        }
      } else if (index === 0) {
        formattedOptions = getOptionsForFirstDropdown(assetTypeDefaults);
      }

      dispatch(targetsiteErp.cascadeDropdownOptions(element.cascade_table_id as string, element.field_id, formattedOptions));
    }
  };

  // Get filtered options for cascading dropdowns
  const getFilteredOptionsForCascading = (index: number, assetTypeDefaults: any) => {
    const previousElement = elementWithCascadeTableId[index - 1];
    const previousValue = previousElement.value?.id;

    if (!previousValue){
      return [];
    } 

    const filteredOptions =
      cascadingStates[element.cascade_table_id as string]?.[element.cascade_table_id?.toLowerCase() ?? '']?.filter(
        (item: any) => item[previousElement.column_name.toLowerCase() + '_id'] === previousValue
      ) || [];

    return formatOptions(filteredOptions, element.column_name);
  };

  // Get options for the first dropdown
  const getOptionsForFirstDropdown = (assetTypeDefaults: any) => {
    const options =
      cascadingStates[element.cascade_table_id as string]?.[element.cascade_table_id?.toLowerCase() ?? ''] || [];

    const formattedOptions = formatOptions(options, element.column_name);

    if (assetTypeDefaults[element.field_id]) {
      const defaultValue = assetTypeDefaults[element.field_id];
      const defaultOption = getDefaultOption(defaultValue, formattedOptions);

      if (defaultOption && isEmpty(assetId)) {
        handleDropdownChange(defaultOption[0]);
      }
    }

    return formattedOptions;
  };

  // Get default option for the first dropdown
  const getDefaultOption = (defaultValue: any[], formattedOptions: any[]) => {
    return defaultValue
      .map((item: any) => {
        const foundOption = formattedOptions.find((option: any) => option.id === item.id);
        return foundOption ? { id: foundOption.id, label: foundOption.label } : null;
      })
      .filter((item: any) => item !== null);
  };

  // Utility function to format dropdown options
  const formatOptions = (options: any[], columnName: string) => {
    return options
      .map((item: any) => ({
        id: item[columnName.toLowerCase() + '_id'],
        label: item[columnName.toLowerCase()],
      }))
      .filter((value: any, index: number, self: any[]) => index === self.findIndex((t) => t.id === value.id));
  };

  const clearCascadeDropdownOptions = (indexValue: number) => {
    if (indexValue !== -1 && indexValue < elementWithCascadeTableId.length - 1) {
      for (let i = indexValue + 1; i < elementWithCascadeTableId.length; i++) {
        const nextElement = elementWithCascadeTableId[i];

        // Clear the value of the next element
        nextElement.value = { id: '', display_value: '' };

        // If the next element has a metadata_element_list, clear its values as well
        if (nextElement.metadata_element_list) {
          nextElement.metadata_element_list.forEach((nextEl: MetadataElement) => {
            nextEl.value = { id: '', display_value: '' };
          });
        }

        // Dispatch an action to update the Redux store for the cleared element
        dispatch(
          singleUploadActions.updateSingleUploadPayload({
            ...singleUploadPayload,
            metadata_collection: {
              ...singleUploadPayload.metadata_collection,
              metadata_element_list: singleUploadPayload.metadata_collection.metadata_element_list.map(
                (el: MetadataElementList) =>
                  el.name === nextElement.name ? nextElement : el
              ),
            },
          })
        );
      }
    }
  };

  // Handle dropdown value change
  const handleDropdownChange = (value: any) => {
    const updatedPayload = singleUploadPayload.metadata_collection.metadata_element_list.find(
      (el: MetadataElement) => el.name === element.name
    );

    if (!updatedPayload) {
      console.warn(`Element with name "${element.name}" not found in metadata_element_list.`);
      return;
    }
    const indexValue = elementWithCascadeTableId.findIndex((e: MetadataElement) => e.field_id === element.field_id);

    updatedPayload.metadata_element_list.forEach((el: MetadataElement) => {
      if (el.field_id === element.field_id) {
        el.value = { id: value?.id, display_value: value?.label };
        updateCascadingDropdownOptions(el, value?.id);
      }
    });
    if(indexValue >0){
      clearCascadeDropdownOptions(indexValue);
    }


    const updatedSingleUploadPayload = {
      ...singleUploadPayload,
      metadata_collection: {
        ...singleUploadPayload.metadata_collection,
        metadata_element_list: singleUploadPayload.metadata_collection.metadata_element_list.map(
          (el: MetadataElementList) => (el.name === updatedPayload.name ? updatedPayload : el)
        ),
      },
    };

    dispatch(singleUploadActions.updateSingleUploadPayload(updatedSingleUploadPayload));
  };

  // Update cascading dropdown options
  const updateCascadingDropdownOptions = (currentElement: MetadataElement, prevVal: string) => {
    const index = elementWithCascadeTableId.findIndex((e: MetadataElement) => e.field_id === currentElement.field_id);

    for (let i = index + 1; i < elementWithCascadeTableId.length; i++) {
      const prev = elementWithCascadeTableId[i - 1];
      const nextElement = elementWithCascadeTableId[i];

      const filteredOptions =
        cascadingStates[element.cascade_table_id as string]?.[element.cascade_table_id?.toLowerCase() ?? '']?.filter(
          (item: any) => item[prev.column_name.toLowerCase() + '_id'] === prevVal
        ) || [];

      const formattedOptions = formatOptions(filteredOptions, nextElement.column_name);

      dispatch(targetsiteErp.cascadeDropdownOptions(element.cascade_table_id as string, nextElement.field_id, formattedOptions));
    }
  };

  // Get error message for validation
  const getErrorMessage = () => {
    if (eventType === 'validate' && !selectedValue && element.is_required === 'Y') {
      return 'This field is required';
    }
    return '';
  };

  // Determine if the dropdown should be disabled
  const getIsDisabled = () => {
    if (element.is_editable === 'Y') {
      return false;
    }
    // if (isProductIdentifiers && isCustomField === 'Y') {
    //   return false;
    // }
    return true;
  };

  useEffect(() => {
    if (element.domain_id) {
      fetchDropdownOptions();
    }
  }, [cascadingStates]);

  const handleErrorClose = (id: string) => {
    //find index of the element with the given id
    const index = elementWithCascadeTableId.findIndex(
      (e: MetadataElement) => e.field_id === element.field_id
    );
    //show alert if the previous dropdown is not selected
    if (elementWithCascadeTableId.length > 1 && index > 0) {
      const previousElement = elementWithCascadeTableId[index - 1];
      if (!previousElement?.value?.id) {
        setAlert({
          show: true,
          message: `Please select a value for ${previousElement?.prompt} before selecting ${element?.prompt}.`,
        });
      }
    }

  };

  const selectedValue =
    element?.values?.length ?? 0 > 1
      ? singleUploadPayload?.metadata_collection?.metadata_element_list
        ?.find((el: MetadataElement) => el.name === element.name)
        ?.metadata_element_list.find((el: MetadataElement) => el.field_id === element.field_id)
        ?.values[rowIndex as number]?.display_value
      : singleUploadPayload?.metadata_collection?.metadata_element_list
        ?.find((el: MetadataElement) => el.name === element.name)
        ?.metadata_element_list.find((el: MetadataElement) => el.field_id === element.field_id)
        ?.value?.display_value;

  const menuSize = element.field_id === 'TFS.FIELD.ERP' || element.field_id === 'TFS.FIELD.CAT_ERP' ? 2 : 4;

  return (
    <>
      <CoreDropdown
        filterable
        wrap
        emptylabel="No options found"
        placeholder="Select"
        functionality="single-select"
        className="dropdown-field"
        size="regular"
        customkey="label"
        options={cascadeDropdownOptions?.[element.field_id] || []}
        selected={isEmpty(selectedValue) ? [] : [selectedValue]}
        disabled={getIsDisabled()}
        onSelectionChanged={(event) => handleDropdownChange(event.detail.option)}
        menusize={menuSize}
        data-testid="dropdown"
        error={getErrorMessage() !== ''}
        errormessage={getErrorMessage()}
        onClick={() => handleErrorClose(element.field_id)}
      />
      {alert.show && (
        <AlertBox
          isModalOpen={alert.show}
          message={alert.message}
          handleClose={() => setAlert({ show: false, message: '' })}
        />
      )}
    </>
  );
};

export default DropdownFieldWrapperCascade;